/* eslint-disable complexity */
import { useCallback, useEffect, useState } from 'react'
import { withCookies } from 'react-cookie'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import PropTypes from 'prop-types'

import { fetchQuiz } from 'redux/actions/QuizActions'
import {
  addAnswerToCurrentAttempt,
  initQuizAttempt,
  sendQuizAnswer,
  setCurrentQuestion,
} from 'redux/actions/QuizAttemptActions'

import Loader from 'components/shared/Loader/Loader'
import Question from './Question/Question'
import Results from './Results/Results'
import { createAnswerHandler } from 'helpers/KnowledgeQuiz'

const KnowledgeQuiz = (props) => {
  const {
    authentication,
    badge,
    cookies,
    locale,
    slug,
    quizPath,
    quizId,
    quizAttempt,
    quizVersion,
    questions = [],
    fetchQuizAction,
    initQuizAttemptAction,
    addQuizAnswerAction,
    sendQuizAnswerAction,
    setCurrentQuestionAction,
    loading,
    project,
    currentStepPosition,
    projectProgress,
    progressCallback,
  } = props

  const searchParams = useSearchParams()[0]
  const branch = searchParams.get('branch')
  const [retryQuizBoolean, setRetryQuizBoolean] = useState(false)

  const isLoading =
    loading || projectProgress?.loading || projectProgress?.sending
      ? true
      : badge?.loading || false

  const sessionId = cookies.get('sessionProgress')
  const { answers, currentQuestion = -1, ...currentAttempt } = quizAttempt

  const answerQuestion = createAnswerHandler(
    quizId,
    currentAttempt,
    currentQuestion,
    addQuizAnswerAction,
    sendQuizAnswerAction
  )

  const nextQuestion = useCallback(() => {
    setCurrentQuestionAction(currentQuestion + 1)
  }, [currentQuestion, setCurrentQuestionAction])

  const [nextQuestionState, setNextQuestionState] = useState(() => nextQuestion)

  useEffect(() => {
    setNextQuestionState(() => nextQuestion)
  }, [currentQuestion, questions, nextQuestion, setNextQuestionState])

  useEffect(() => {
    setRetryQuizBoolean(false)
  }, [])

  const showFinalResults = currentQuestion === questions.length
  const { quiz_length: quizLength = 0 } = currentAttempt || {}

  // authentication.user may be null, which means it is defined...
  const { profile: { user: userId = null } = {} } = authentication.user || {}

  const retryQuiz = () => {
    setRetryQuizBoolean(true)
    initQuizAttemptAction(
      locale,
      questions.length,
      quizVersion,
      userId,
      sessionId
    )
  }

  const questionsOrResults = () => (
    <div className="knowledge-quiz">
      {isLoading && <div></div>}
      {!isLoading &&
        (showFinalResults || (badge?.issued && !retryQuizBoolean) ? (
          <Results
            authentication={authentication}
            badge={badge}
            loading={isLoading}
            project={project}
            quizAttempt={quizAttempt}
            currentStepPosition={currentStepPosition}
            retryQuiz={retryQuiz}
            slug={slug}
          />
        ) : (
          <Question
            answers={answers}
            content={questions[currentQuestion]}
            currentQuestion={currentQuestion}
            answerQuestion={answerQuestion}
            nextQuestion={nextQuestionState}
            quizLength={questions.length}
            project={project}
            locale={locale}
          />
        ))}
    </div>
  )

  useEffect(() => {
    fetchQuizAction(locale, slug, quizPath, branch)
  }, [fetchQuizAction, locale, slug, quizPath, branch])

  useEffect(() => {
    const { steps = [] } = project
    const currentStep = steps[currentStepPosition]

    if (
      !project ||
      !quizLength ||
      !showFinalResults ||
      !currentStep ||
      projectProgress.currentStep === currentStepPosition ||
      projectProgress.loading ||
      projectProgress.sending
    ) {
      return
    }
    progressCallback()
  }, [
    project,
    quizLength,
    projectProgress,
    currentStepPosition,
    showFinalResults,
    progressCallback,
  ])

  useEffect(() => {
    if ((!loading && quizId && sessionId) || retryQuizBoolean) {
      initQuizAttemptAction(
        locale,
        questions.length,
        quizVersion,
        userId,
        sessionId
      )
    }
  }, [
    initQuizAttemptAction,
    loading,
    locale,
    questions,
    quizVersion,
    quizId,
    userId,
    sessionId,
    retryQuizBoolean,
  ])

  return <>{loading ? <Loader delay={200} /> : questionsOrResults()}</>
}

KnowledgeQuiz.propTypes = {
  cookies: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  quizPath: PropTypes.string.isRequired,
  project: PropTypes.object.isRequired,
  currentStepPosition: PropTypes.number,
  projectProgress: PropTypes.object,
  fetchQuizAction: PropTypes.func.isRequired,
  initQuizAttemptAction: PropTypes.func.isRequired,
  addQuizAnswerAction: PropTypes.func.isRequired,
  sendQuizAnswerAction: PropTypes.func.isRequired,
  setCurrentQuestionAction: PropTypes.func.isRequired,
  authentication: PropTypes.object.isRequired,
  progressCallback: PropTypes.func.isRequired,
  error: PropTypes.object,
  loading: PropTypes.bool,
  questions: PropTypes.array,
  quizId: PropTypes.number,
  quizVersion: PropTypes.number,
  quizAttempt: PropTypes.object,
  badge: PropTypes.object,
}

const mapStateToProps = (state) => {
  const {
    error,
    loading,
    questions,
    id: quizId,
    version: quizVersion,
  } = state.quiz
  const quizAttempt = state.quizAttempt
  const {
    authentication,
    badge,
    projectProgress,
    project: { currentStepPosition },
  } = state
  return {
    authentication,
    badge,
    error,
    loading,
    questions,
    quizId,
    quizVersion,
    quizAttempt,
    projectProgress,
    currentStepPosition,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchQuizAction: (locale, slug, quizPath, branch = 'master') =>
      dispatch(
        fetchQuiz({
          currentLocale: locale,
          currentProject: slug,
          quizPath,
          branch,
        })
      ),
    initQuizAttemptAction: (
      currentLocale,
      quizLength,
      quizVersion,
      userId,
      sessionId
    ) =>
      dispatch(
        initQuizAttempt({
          currentLocale,
          quizLength,
          quizVersion,
          userId,
          sessionId,
        })
      ),
    addQuizAnswerAction: (newAnswer, attempt) =>
      dispatch(addAnswerToCurrentAttempt(newAnswer, attempt)),
    sendQuizAnswerAction: (quizId, newAnswer, attempt) =>
      dispatch(sendQuizAnswer(quizId, newAnswer, attempt)),
    setCurrentQuestionAction: (questionNumber) =>
      dispatch(setCurrentQuestion(questionNumber)),
  }
}

export { KnowledgeQuiz }
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withCookies(KnowledgeQuiz))
